import React, { useMemo } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { Divider, DividerProps, Tab, Tabs } from "@mui/material";
import { ReactComponent as PortfolioIcon } from "assets/icons/portfolio-overview.svg";
import { AssetReportIcon, WorkspaceNewsIcon } from "assets/material-custom-icons";
import { useAccountSelector } from "store";

// Utility function to detect operating system
const getOperatingSystem = (): "windows" | "macos" | "linux" | "unknown" => {
  if (typeof window === "undefined") return "unknown";

  const userAgent = window.navigator.userAgent.toLowerCase();

  if (userAgent.includes("win")) return "windows";
  if (userAgent.includes("mac")) return "macos";
  if (userAgent.includes("linux")) return "linux";

  return "unknown";
};

export interface FormTabsProps {
  categories: string[];
  value: string;
  onChange: (newValue: string) => void;
}

const dividerProps: DividerProps = {
  orientation: "vertical",
  sx: { width: "1px", height: "16px", bgcolor: "#dae0e2", margin: "0 16px 0 16px" }
};

type CategoryTab = { name: string; icon?: JSX.Element };

const icons: Record<string, JSX.Element> = {
  General: <InfoOutlinedIcon sx={{ fill: "#0a3142", height: "15px", marginRight: "8px" }} />,
  Legal: <WorkspaceNewsIcon sx={{ fill: "#0a3142", height: "15px", marginRight: "8px" }} />,
  Financial: <PortfolioIcon style={{ fill: "#0a3142", height: "15px", marginRight: "8px" }} />
};

export const CategoriesTabs: React.FC<FormTabsProps> = ({ categories, value, onChange }) => {
  const { single_asset_report } = useAccountSelector() || {};
  const os = getOperatingSystem();
  const dividedTabs: Array<CategoryTab | null> = useMemo(() => {
    const result: Array<CategoryTab | null> = [];

    categories.forEach((item, index) => {
      const icon = icons[item];

      if (index) {
        result.push(null);
      }

      result.push({ icon, name: item });
    });

    result.push(null);
    result.push({
      icon: <PersonOutlineOutlinedIcon sx={{ fill: "#0a3142", height: "15px", marginRight: "8px" }} />,
      name: "Contact"
    });
    if (single_asset_report) {
      result.push(null);
      result.push({
        icon: <AssetReportIcon sx={{ fill: "#0a3142", height: "15px", marginRight: "8px" }} />,
        name: "Asset Report"
      });
    }

    return result;
  }, [categories, single_asset_report]);

  const validValue = useMemo(() => {
    const tabNames = dividedTabs.filter((tab): tab is CategoryTab => tab !== null).map((tab) => tab.name);
    return tabNames.includes(value) ? value : tabNames[0];
  }, [dividedTabs, value]);

  return (
    <Tabs
      variant="scrollable"
      scrollButtons={os === "windows"}
      allowScrollButtonsMobile={os === "windows"}
      sx={{
        pr: "20px",
        minHeight: "24px",
        button: {
          p: 0,
          minHeight: "20px",
          fontWeight: "normal",
          color: "#5d7782",
          fontSize: "15px"
        },
        ".MuiTabs-flexContainer": {
          alignItems: "center",
          gap: "6px"
        },
        ".MuiTab-root": {
          justifyContent: "flex-start",
          alignItems: "center",
          minWidth: "auto",
          fontSize: "14px",
          fontWeight: "500",
          "&.Mui-selected": {
            color: "#071F35"
          }
        },
        "& .MuiTabs-indicator": {
          display: "flex",
          justifyContent: "center",
          backgroundColor: "transparent",
          "& > span": {
            width: "98%",
            backgroundColor: "#071F35"
          }
        },
        "& .MuiTabs-scrollButtons": {
          color: "#5d7782",
          "&.Mui-disabled": {
            opacity: 0.3
          }
        }
      }}
      TabIndicatorProps={{
        children: <span />
      }}
      value={validValue}
      onChange={(_: React.SyntheticEvent, newValue) => onChange(newValue)}
    >
      {dividedTabs.map((tab, index) => {
        if (!tab) return <Divider key={index} {...dividerProps} />;
        return <Tab key={String(tab.name)} label={tab.name} icon={tab.icon} value={tab.name} iconPosition="start" />;
      })}
    </Tabs>
  );
};
